<template>
  <section
    class="format-comparison"
    aria-labelledby="format-comparison-heading"
  >
    <div class="format-comparison__header">
      <div
        class="format-comparison__icon"
        v-html="LAPTOP_ICON_SVG"
        aria-hidden="true"
      ></div>
      <div class="format-comparison__heading-row">
        <h2 id="format-comparison-heading" class="format-comparison__title">
          Choose How You Want to Start Making
        </h2>
        <p class="format-comparison__intro">
          Whether you’re introducing one focused set of activities or building a
          more comprehensive classroom makerspace, choose the setup that works
          for your learners.
        </p>
      </div>
    </div>
    <div class="format-comparison__body">
      <div class="format-comparison__cards">
        <article
          v-for="format in formats"
          :key="format.id"
          :class="['format-card', `format-card--${format.id}`]"
        >
          <span class="format-card__badge">{{ format.badge }}</span>
          <div class="format-card__body">
            <div class="format-card__heading">
              <h3 class="format-card__title">{{ format.title }}</h3>
              <p class="format-card__subtitle">{{ format.subtitle }}</p>
            </div>
            <div class="format-card__divider" aria-hidden="true"></div>
            <p class="format-card__description">{{ format.description }}</p>
            <ul class="format-card__features">
              <li
                v-for="feature in format.features"
                :key="feature"
                class="format-card__feature"
              >
                <span class="format-card__check">
                  <img :src="format.checkIcon" alt="" width="24" height="24" />
                </span>
                <span class="format-card__feature-text">{{ feature }}</span>
              </li>
            </ul>
          </div>
          <ActionButton
            variant="outline"
            size="large"
            full-width
            type="button"
            :customClass="
              format.id === 'crate' ? 'format-card__button--crate' : ''
            "
            @click="router.push(format.route)"
          >
            {{ format.buttonText }}
            <template #icon-right>
              <ArrowShortRightIcon color="currentColor" />
            </template>
          </ActionButton>
        </article>
      </div>
      <p class="format-comparison__footer">
        Start with a focused kit or scale across a larger classroom, the choice
        depends on where you are in your maker-learning journey.
      </p>
    </div>
  </section>
</template>

<script setup>
import { useRouter } from "vue-router";
import ActionButton from "@/components/common/ActionButton.vue";
import { ArrowShortRightIcon } from "@/components/icons";

const router = useRouter();

const LAPTOP_ICON_SVG = `<svg width="68" height="51" viewBox="0 0 68 51" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M0.666687 44C0.666687 42.159 2.15907 40.6666 4.00002 40.6666H64C65.841 40.6666 67.3334 42.159 67.3334 44V47.3333C67.3334 49.1742 65.841 50.6666 64 50.6666H4.00002C2.15907 50.6666 0.666687 49.1742 0.666687 47.3333V44Z" fill="#E00069"/>
  <path d="M64 40.6666V40.9049C63.9999 42.6142 62.5077 43.9995 60.667 43.9996H7.33301C5.49228 43.9995 4.00011 42.6142 4 40.9049V40.6666H64ZM20.5664 0.666626C22.133 0.666626 23.4358 1.91968 24.6777 2.87463C25.3986 3.42885 26.324 3.76226 27.333 3.76233H40.667C41.676 3.76226 42.6014 3.42885 43.3223 2.87463C44.5642 1.91968 45.867 0.666626 47.4336 0.666626H60.667C62.5078 0.66679 64 2.05298 64 3.76233V35.6666H4V3.76233C4 2.05298 5.49221 0.666789 7.33301 0.666626H20.5664Z" fill="#E00069"/>
</svg>`;

const formats = [
  {
    id: "cart",
    title: "Makercart",
    subtitle: "A mobile makerspace built to scale",
    description:
      "A customizable, classroom-ready setup with organized materials and teaching resources for hands-on, project-based learning.",
    badge: "Starting at $3,000",
    features: [
      "Supports up to 80 students across multiple class periods",
      "Choose from 120+ activities through Eddy digital platform",
      "Materials and curriculum resources included",
      "Professional development and implementation support available upon request",
    ],
    buttonText: "Explore Makercart",
    route: { name: "MakerCartPage" },
    checkIcon: "/images/maker-cart/get-started/check-icon.svg",
  },
  {
    id: "crate",
    title: "Makercrate",
    subtitle: "A focused way to start small",
    description:
      "Pre-configured activity kits that give educators the materials and resources needed to begin making without building a complete makerspace.",
    badge: "Starting at $299",
    features: [
      "Supports up to 20 students per crate kit",
      "Two to four curated activities, depending on the kit",
      "Materials and print and digital resources included",
      "Starter, intermediate and advanced options",
    ],
    buttonText: "Explore Makercrate",
    route: { name: "MakerCartPage", query: { tab: "makercrate" } },
    checkIcon: "/images/maker-cart/get-started/check-icon-crate.svg",
  },
];
</script>

<style scoped>
.format-comparison {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 100%;
  padding: 40px 0;
}

.format-comparison__header {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.format-comparison__icon {
  display: flex;
  justify-content: left;
  width: 100%;
  height: 80px;
}

.format-comparison__heading-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.format-comparison__title {
  flex: 1;
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-display-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-small);
  color: var(--blue-dark-2);
}

.format-comparison__intro {
  flex: 0 0 628px;
  max-width: 628px;
  margin: 0;
  text-align: right;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-title-large);
  color: var(--black-body-text);
}

.format-comparison__body {
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  width: 100%;
}

.format-comparison__cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 40px;
  width: 100%;
}

.format-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 24px;
  overflow: visible;
  border-radius: 24px;
  background-color: var(--white);
  box-sizing: border-box;
}

.format-card--cart {
  border: 2px solid var(--blue-light-1);
}

.format-card--crate {
  border: 2px solid var(--purple-light);
}

.format-card__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  padding: 6px 16px;
  background-color: var(--blue-light-1);
  color: var(--white);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-small);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-body-small);
  letter-spacing: 0.048px;
  border-radius: 0 24px 0 24px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
}

.format-card--crate .format-card__badge {
  background-color: var(--purple-light);
}

.format-card__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.format-card__heading {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.format-card__title {
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-display-medium);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-medium);
}

.format-card--cart .format-card__title,
.format-card--cart .format-card__subtitle {
  color: var(--primary-blue);
}

.format-card--crate .format-card__title,
.format-card--crate .format-card__subtitle {
  color: var(--primary-purple);
}

.format-card__subtitle {
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-small);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-headline-small);
}

.format-card__divider {
  width: 100%;
  border-bottom: 1px dashed var(--border-dashed);
}

.format-card--crate .format-card__divider {
  border-bottom-color: var(--purple-light-2);
}

.format-card__description {
  margin: 0;
  min-height: 84px;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-title-large);
  color: var(--black-body-text);
}

.format-card__features {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.format-card__feature {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.format-card__check {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  overflow: clip;
}

.format-card__check img {
  width: 24px;
  height: 24px;
}

.format-card__feature-text {
  flex: 1;
  min-width: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-big);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-title-big);
  letter-spacing: 0.03px;
  color: var(--black-body-text);
}

.format-card--crate :deep(.format-card__button--crate) {
  color: var(--primary-purple);
  border-color: var(--primary-purple);
}

.format-card--crate :deep(.format-card__button--crate:hover:not(:disabled)) {
  background-color: var(--primary-purple);
  color: var(--white);
  border-color: var(--primary-purple);
}

.format-comparison__footer {
  margin: 0;
  width: 100%;
  text-align: center;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-headline-large);
  color: var(--blue-dark-2);
}

@media (max-width: 1200px) {
  .format-comparison {
    padding: 40px 0;
  }

  .format-comparison__intro {
    flex: 1;
    max-width: none;
  }

  .format-comparison__cards {
    gap: 24px;
  }

  .format-card__title {
    font-size: 38px;
    line-height: 44px;
  }

  .format-comparison__footer {
    font-size: var(--fs-xxl);
    line-height: 36px;
  }
}

@media (max-width: 992px) {
  .format-comparison__heading-row {
    flex-direction: column;
    gap: 16px;
  }

  .format-comparison__intro {
    text-align: left;
  }

  .format-comparison__cards {
    grid-template-columns: 1fr;
  }

  .format-card__title {
    font-size: var(--fs-3xl);
    line-height: 42px;
  }

  .format-card__description {
    min-height: 0;
  }
}

@media (max-width: 768px) {
  .format-comparison {
    gap: 24px;
    padding: 16px 0;
    align-items: stretch;
  }

  .format-comparison__header {
    gap: 24px;
  }

  .format-comparison__icon {
    height: 40px;
  }

  .format-comparison__icon :deep(svg) {
    width: auto;
    height: 40px;
  }

  .format-comparison__heading-row {
    gap: 16px;
  }

  .format-comparison__title {
    font-size: var(--font-size-title-large);
    line-height: var(--line-height-title-large);
    font-weight: var(--font-weight-bold);
    color: var(--blue-dark-2);
  }

  .format-comparison__intro {
    font-size: var(--font-size-title-medium);
    line-height: var(--line-height-title-medium);
    font-weight: var(--font-weight-regular);
    letter-spacing: 0.024px;
    color: var(--black-body-text);
  }

  .format-comparison__body {
    gap: 18px;
  }

  .format-comparison__cards {
    gap: 24px;
  }

  .format-card {
    padding: 24px;
  }

  .format-card__title {
    font-size: var(--font-size-display-medium);
    line-height: var(--line-height-display-medium);
    font-weight: var(--font-weight-bold);
  }

  .format-card--cart .format-card__title,
  .format-card--cart .format-card__subtitle {
    color: var(--primary-blue);
  }

  .format-card--crate .format-card__title,
  .format-card--crate .format-card__subtitle {
    color: var(--primary-purple);
  }

  .format-card__subtitle {
    font-size: var(--font-size-headline-small);
    line-height: var(--line-height-headline-small);
    font-weight: var(--font-weight-regular);
  }

  .format-card__description {
    font-size: var(--font-size-title-large);
    line-height: var(--line-height-title-large);
    font-weight: var(--font-weight-medium);
    color: var(--black-body-text);
  }

  .format-card__feature-text {
    font-size: var(--font-size-title-big);
    line-height: var(--line-height-title-big);
    font-weight: var(--font-weight-regular);
    letter-spacing: 0.03px;
    color: var(--black-body-text);
  }

  .format-card :deep(.action-button.action-button--large) {
    min-height: 56px;
    height: 56px;
    padding: 10px 16px;
    font-size: var(--font-size-title-big);
    line-height: var(--line-height-title-big);
  }

  .format-card :deep(.action-button.action-button--large .action-button__text) {
    font-size: var(--font-size-title-big);
    line-height: var(--line-height-title-big);
    letter-spacing: 0.03px;
    font-weight: var(--font-weight-bold);
    color: inherit;
  }

  .format-comparison__footer {
    font-size: var(--font-size-title-big);
    line-height: var(--line-height-title-big);
    font-weight: var(--font-weight-medium);
    letter-spacing: 0.03px;
    color: var(--blue-dark-2);
  }
}
</style>
